import {
  ComponentProps,
  FC,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react'
import Modal from '@common/Modal'
import EditStudentModal from '@pages/Admin/AllUsers/modals/EditStudentModal/EditStudentModal'
import EditParentModal from '@pages/Admin/AllUsers/modals/EditParentModal'
import { default as BaseLeadProfileModal } from '@pages/Admin/CRM/components/LeadProfileModal/LeadProfileModal'
import classes from './index.module.css'
import { CRMProvider, useCheckCRMContext } from '@pages/Admin/CRM/CRMContext'
import { Lead } from '@/api/types/crm.types'
import { crmApi } from '@/api'
import cn from 'classnames'
import CollegesItemEdit from '@pages/Admin/AllUsers/Colleges/CollegesItemEdit'

type EditEntityModalProps = EditEntityModalBaseProps & OptionsModalProps

type OptionsModalProps =
  | EditStudentModalProps
  | EditParentModalProps
  | EditLeadModalProps
  | EditEmployeeModalProps

interface EditStudentModalProps {
  type: 'student'
  studentId?: string | number | null
}

interface EditParentModalProps {
  type: 'parent'
  parentId?: string | number | null
}
interface EditEmployeeModalProps {
  type: 'employee'
  employeeId: string | number | null
}

type EditLeadModalProps = LeadProps & {
  type: 'lead'
}

type LeadProps =
  | {
      leadId: string | number
    }
  | {
      lead?: Lead
    }

interface EditEntityModalBaseProps {
  classNames?: {
    studentModal?: string
    studentModalBackground?: string
    parentModal?: string
    parentModalBackground?: string
  }
  open: boolean
  onDismiss: () => void
  onUpdate: () => void
}

const EditEntityModal: FC<EditEntityModalProps> = ({
  type,
  open,
  onDismiss,
  onUpdate,
  classNames,
  ...props
}) => {
  const [openModal, setOpenModal] = useState<
    OptionsModalProps | { type: null }
  >({
    type: null,
  })

  const memoizedProps = useMemo(() => {
    if (type === 'student') return { studentId: (props as any).studentId }
    if (type === 'parent') return { parentId: (props as any).parentId }
    if (type === 'lead') {
      return {
        ...('leadId' in props ? { leadId: props.leadId } : {}),
        ...('lead' in props ? { lead: props.lead } : {}),
      }
    }
    if (type === 'employee') return { employeeId: (props as any).employeeId }
    return {}
  }, [
    type,
    type === 'student' ? (props as any).studentId : null,
    type === 'parent' ? (props as any).parentId : null,
    type === 'lead' && 'leadId' in props ? props.leadId : null,
    type === 'lead' && 'lead' in props ? props.lead : null,
    type === 'employee' && 'employee' in props ? props.employee : null,
  ])

  useEffect(() => {
    if (type) setOpenModal({ type, ...memoizedProps } as OptionsModalProps)
  }, [type, memoizedProps])

  const closeModal = useCallback(() => {
    setOpenModal({ type: null })
    onDismiss()
  }, [onDismiss])

  const closeModalAndUpdate = useCallback(() => {
    setOpenModal({ type: null })
    onUpdate()
  }, [onUpdate])

  return (
    <>
      <Modal
        active={open && openModal.type === 'student' && !!openModal.studentId}
        setActive={closeModal}
        // @ts-ignore
        className={cn(classes.fullModal, classNames?.studentModal)}
        classNameBackground={classNames?.studentModalBackground}
      >
        <EditStudentModal
          // @ts-ignore
          studentId={openModal.studentId}
          onDismiss={closeModal}
          onUpdate={closeModalAndUpdate}
          // @ts-ignore
          onClickByParent={(parent: any) =>
            setOpenModal({ type: 'parent', parentId: parent.id })
          }
          // @ts-ignore
          onClickByLead={(leadId) => setOpenModal({ type: 'lead', leadId })}
        />
      </Modal>
      <Modal
        active={open && openModal.type === 'parent' && !!openModal.parentId}
        setActive={closeModal}
        // @ts-ignore
        className={cn(classes.fullModal, classNames?.parentModal)}
        classNameBackground={classNames?.parentModalBackground}
      >
        <EditParentModal
          // @ts-ignore
          parentId={openModal.parentId}
          onDismiss={closeModal}
          onUpdate={closeModalAndUpdate}
          // @ts-ignore
          onClickByChildren={(child: any) =>
            setOpenModal({ type: 'student', studentId: child.id })
          }
        />
      </Modal>
      <LeadProfileModal
        open={open && openModal.type === 'lead'}
        lead={
          openModal.type === 'lead'
            ? 'lead' in openModal
              ? openModal.lead
              : 'leadId' in openModal
              ? openModal.leadId
              : undefined
            : undefined
        }
        onClose={(withChanges) =>
          withChanges ? closeModalAndUpdate() : closeModal()
        }
        onClickByChildren={(userId) =>
          setOpenModal({ type: 'student', studentId: userId })
        }
      />
      <Modal
        active={open && openModal.type === 'employee' && !!openModal.employeeId}
        setActive={closeModal}
        // @ts-ignore
        className={cn(classes.fullModal, classNames?.studentModal)}
        classNameBackground={classNames?.studentModalBackground}
      >
        <CollegesItemEdit
          // @ts-ignore
          big
          setEdit={closeModal}
          // @ts-ignore
          edit={openModal.employeeId}
          setState={null}
        />
      </Modal>
    </>
  )
}

// ---------- LEAD MODAL -----------

interface LeadProfileModalProps
  extends Omit<ComponentProps<typeof BaseLeadProfileModal>, 'lead'> {
  lead?: Lead | number | string
}

const isLeadObject = (obj: any): obj is Lead => {
  return obj && (typeof obj.id === 'string' || typeof obj.id === 'number')
}

const LeadProfileModal: FC<LeadProfileModalProps> = ({
  lead: defLead,
  ...props
}) => {
  const hasCRMContext = useCheckCRMContext()
  const [lead, setLead] = useState<Lead | undefined>()

  useEffect(() => {
    setLead(isLeadObject(defLead) ? defLead : undefined)
    if (!isLeadObject(defLead) && defLead) {
      crmApi
        .getLead({ lead_id: Number(defLead) })
        .then((res) => {
          setLead(res.data?.data)
        })
        .catch(() => console.error('Error fetching lead'))
    }
  }, [defLead])

  useEffect(() => {
    // Функция для удаления всех стилей rubberLayout
    const removeAllRubberLayoutStyles = () => {
      // Удаляем класс с body
      const body = document.querySelector('body')
      if (body?.classList.contains('rubberLayout')) {
        body.classList.remove('rubberLayout')
      }
    }

    // Вызываем функцию очистки
    removeAllRubberLayoutStyles()

    // Также устанавливаем интервал для периодической очистки (на случай если стили применяются динамически)
    const intervalId = setInterval(removeAllRubberLayoutStyles, 100)

    // Очищаем интервал при размонтировании
    return () => {
      clearInterval(intervalId)
      removeAllRubberLayoutStyles()
    }
  }, [])

  if (hasCRMContext) return <BaseLeadProfileModal {...props} lead={lead} />

  return (
    <CRMProvider currentTab={lead?.type ?? 'new'}>
      <BaseLeadProfileModal {...props} lead={lead} />
    </CRMProvider>
  )
}

export default EditEntityModal
